import { LOGO_ALLOWED_MIME_TYPES } from "@etudescarre/constants";
import { Button, cn, Modal } from "@etudescarre/ui";
import { isNil } from "@etudescarre/utils";
import {
  ImageIcon,
  ListBulletsIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import { type ChangeEvent, useCallback, useRef, useState } from "react";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { cellInputClass as inputClass } from "@/lib/utils/cell-input";
import {
  confirmDiscardDraft,
  useGuardedNavigate,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";
import {
  AdminListPagination,
  AdminListSearch,
  useAdminListQuery,
} from "./components";

type Company = {
  id: string;
  name: string;
  logoUrl: string | null;
  createdAt: string;
};

type NewCompanyRow = { tempId: string; name: string };

function generateTempId(): string {
  return `tmp-${Math.random().toString(36).slice(2)}`;
}

/**
 * Screen 902 — companies directory as an inline-editable draft table
 * (same pattern as screens 904/909/910): the name is edited in place and
 * staged until "Enregistrer"; "+ Ajouter" rows create new companies.
 */
export function CompaniesList() {
  const navigate = useGuardedNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Draft edit mode: pending name edits keyed by company id, plus
  // not-yet-saved new rows. Highlighted until Enregistrer/Annuler.
  const [draft, setDraft] = useState<Map<number, string>>(new Map());
  const [newRows, setNewRows] = useState<NewCompanyRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Logo upload: uploads immediately on file selection (not staged in the
  // draft) since it has no text value to edit in place.
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoTargetId, setLogoTargetId] = useState<number | null>(null);
  const [uploadingLogoId, setUploadingLogoId] = useState<number | null>(null);

  const fetcher = useCallback(
    async ({
      search,
      page,
      pageSize,
    }: {
      search?: string;
      page: number;
      pageSize: number;
    }) => {
      const { data, error } = await apiClient.GET("/api/admin/companies", {
        params: {
          query: {
            search: search ?? undefined,
            page,
            pageSize,
          },
        },
      });
      if (error) {
        throw new Error("Erreur lors du chargement des entreprises");
      }
      return {
        items: data.data.companies.map((c) => ({
          ...c,
          id: String(c.id),
        })),
        total: data.data.total,
      };
    },
    [],
  );

  const {
    items: companies,
    total,
    loading,
    error,
    search,
    page,
    pageSize,
    setSearch,
    setPage,
    setPageSize,
    refetch,
  } = useAdminListQuery<Company>(fetcher, "companies");

  const isDirty = draft.size > 0 || newRows.length > 0;
  useUnsavedChangesGuard(isDirty);

  const effectiveName = (company: Company) =>
    draft.get(Number(company.id)) ?? company.name;

  const isDirtyRow = (company: Company) => draft.has(Number(company.id));

  const onNameChange = (company: Company, name: string) => {
    const id = Number(company.id);
    setDraft((prev) => {
      const updated = new Map(prev);
      if (name === company.name) updated.delete(id);
      else updated.set(id, name);
      return updated;
    });
  };

  const cancelDraft = () => {
    setDraft(new Map());
    setNewRows([]);
  };

  const save = async () => {
    if (
      newRows.some((row) => !row.name.trim()) ||
      [...draft.values()].some((name) => !name.trim())
    ) {
      toaster.error({
        title: "Erreur",
        description: "Le nom d'une entreprise ne peut pas être vide.",
      });
      return;
    }
    setIsSaving(true);
    let failed = false;
    for (const [id, name] of draft.entries()) {
      const { error: apiError } = await apiClient.PATCH(
        "/api/admin/companies/{id}",
        { params: { path: { id } }, body: { name: name.trim() } },
      );
      if (apiError) failed = true;
    }
    for (const row of newRows) {
      const { error: apiError } = await apiClient.POST("/api/admin/companies", {
        body: { name: row.name.trim() },
      });
      if (apiError) failed = true;
    }
    setIsSaving(false);
    if (failed) {
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement de certaines modifications",
      });
    } else {
      toaster.success({
        title: "Succès",
        description: "Modifications enregistrées",
      });
    }
    setDraft(new Map());
    setNewRows([]);
    refetch();
  };

  const triggerLogoUpload = (company: Company) => {
    setLogoTargetId(Number(company.id));
    logoInputRef.current?.click();
  };

  const handleLogoFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || isNil(logoTargetId)) return;

    setUploadingLogoId(logoTargetId);
    try {
      const { error: apiError } = await apiClient.POST(
        "/api/admin/companies/{id}/logo",
        {
          params: { path: { id: logoTargetId } },
          body: { logo: file },
          bodySerializer(body) {
            const fd = new FormData();
            if (body?.logo instanceof Blob) fd.set("logo", body.logo);
            return fd;
          },
        },
      );
      if (apiError) {
        toaster.error({
          title: "Logo non enregistré",
          description: apiError.error || "Le logo n'a pas pu être téléversé",
        });
      } else {
        toaster.success({ title: "Logo mis à jour" });
        refetch();
      }
    } catch {
      toaster.error({
        title: "Logo non enregistré",
        description: "Le logo n'a pas pu être téléversé",
      });
    } finally {
      setUploadingLogoId(null);
      setLogoTargetId(null);
    }
  };

  const handleDelete = (company: Company) => {
    setDeletingCompany(company);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingCompany) return;

    setIsDeleting(true);
    const { error: apiError } = await apiClient.DELETE(
      "/api/admin/companies/{id}",
      {
        params: { path: { id: Number(deletingCompany.id) } },
      },
    );

    if (apiError) {
      toaster.error({
        title: "Erreur",
        description:
          apiError.error || "Échec de la suppression de l'entreprise",
      });
      setIsDeleting(false);
      return;
    }

    toaster.success({
      title: "Succès",
      description: "Entreprise supprimée avec succès",
    });

    setDeleteModalOpen(false);
    setDeletingCompany(null);
    setIsDeleting(false);
    refetch();
  };

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-6">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Entreprises
          </h2>
          <div className="flex items-center gap-2">
            {isDirty && (
              <>
                <span className="text-sm text-amber-600 dark:text-amber-400">
                  Modifications non enregistrées
                </span>
                <Button
                  outline
                  onClick={() => void confirmDiscardDraft(cancelDraft)}
                  disabled={isSaving}
                >
                  Annuler
                </Button>
                <Button onClick={save} disabled={isSaving}>
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </>
            )}
            <Button
              onClick={() =>
                setNewRows((prev) => [
                  ...prev,
                  { tempId: generateTempId(), name: "" },
                ])
              }
            >
              <PlusIcon data-slot="icon" className="size-4" />
              Ajouter
            </Button>
          </div>
        </div>

        <div className="mb-4">
          <AdminListSearch
            value={search}
            onChange={setSearch}
            placeholder="Rechercher par nom..."
            label="Recherche"
            id="companies-search"
          />
        </div>

        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center text-gray-500">
            Chargement...
          </div>
        ) : companies.length === 0 && newRows.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center text-gray-500">
            {search ? "Aucun résultat pour cette recherche" : "Aucune donnée"}
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col />
                <col className="w-36" />
                <col className="w-32" />
              </colgroup>
              <thead>
                <tr className="bg-zinc-50 text-left dark:bg-zinc-900">
                  <th className="truncate p-2 font-medium">Nom</th>
                  <th className="truncate p-2 font-medium">Logo</th>
                  <th className="truncate p-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => {
                  const dirty = isDirtyRow(company);
                  return (
                    <tr
                      key={company.id}
                      className={cn(
                        "border-t border-zinc-100 dark:border-zinc-800",
                        dirty && "bg-amber-50 dark:bg-amber-900/20",
                      )}
                    >
                      <td className="p-2">
                        <input
                          type="text"
                          value={effectiveName(company)}
                          onChange={(e) =>
                            onNameChange(company, e.target.value)
                          }
                          className={inputClass(dirty)}
                          placeholder="Nom de l'entreprise"
                        />
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-2">
                          {company.logoUrl ? (
                            <img
                              src={company.logoUrl}
                              alt=""
                              className="size-8 shrink-0 rounded border border-zinc-200 object-contain dark:border-zinc-700"
                            />
                          ) : (
                            <span className="flex size-8 shrink-0 items-center justify-center rounded border border-dashed border-zinc-200 text-zinc-300 dark:border-zinc-700 dark:text-zinc-600">
                              <ImageIcon className="size-4" />
                            </span>
                          )}
                          <Button
                            plain
                            onClick={() => triggerLogoUpload(company)}
                            disabled={uploadingLogoId === Number(company.id)}
                            aria-label="Modifier le logo"
                          >
                            {uploadingLogoId === Number(company.id)
                              ? "..."
                              : "Modifier"}
                          </Button>
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          <Button
                            plain
                            onClick={() =>
                              navigate(
                                `/admin/companies/${company.id}/statuses`,
                              )
                            }
                            aria-label="Statuts"
                          >
                            <ListBulletsIcon className="size-4" />
                          </Button>
                          <Button
                            plain
                            onClick={() => handleDelete(company)}
                            aria-label="Supprimer"
                          >
                            <TrashIcon className="size-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {newRows.map((row) => (
                  <tr
                    key={row.tempId}
                    className="border-t border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20"
                  >
                    <td className="p-2">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) =>
                          setNewRows((prev) =>
                            prev.map((r) =>
                              r.tempId === row.tempId
                                ? { ...r, name: e.target.value }
                                : r,
                            ),
                          )
                        }
                        className={inputClass(true)}
                        placeholder="Nom de l'entreprise"
                      />
                    </td>
                    <td className="p-2 text-xs text-zinc-400">
                      Après enregistrement
                    </td>
                    <td className="p-2">
                      <Button
                        plain
                        onClick={() =>
                          setNewRows((prev) =>
                            prev.filter((r) => r.tempId !== row.tempId),
                          )
                        }
                        aria-label="Annuler l'ajout de l'entreprise"
                      >
                        <XIcon className="size-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AdminListPagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />

        <input
          ref={logoInputRef}
          type="file"
          accept={LOGO_ALLOWED_MIME_TYPES.join(",")}
          className="hidden"
          onChange={handleLogoFileChange}
        />
      </div>

      <Modal.Root
        open={deleteModalOpen}
        onOpenChange={(details) => setDeleteModalOpen(details.open)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Confirmer la suppression</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Modal.Description>
                  Êtes-vous sûr de vouloir supprimer l'entreprise "
                  {deletingCompany?.name}" ? Cette action est irréversible.
                </Modal.Description>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  outline
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleting}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  onClick={confirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Suppression..." : "Supprimer"}
                </Button>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>
    </>
  );
}

export type CompaniesListProps = Record<string, never>;
